import React from 'react'
import { Provider, connect } from 'react-redux'
import { withRouter } from 'react-router'
import qs from 'query-string'
import loadable from '@loadable/component'
import { Toaster } from 'react-hot-toast'
import Head from './Head'
import Main from './Main'
import compose from 'client/lib/helpers/compose'
import * as analytics from 'client/lib/analytics'
import * as crisp from 'client/lib/crisp'
import * as rollbar from 'client/lib/rollbar'
import { initializePostHog } from 'client/lib/posthog'
import {
  loadUser, loadBrowser, toggleModal, setPageError,
  handleReferral, extensionLoaded, checkOrganizationLicense,
  setBannerContent, loadAnonToken
} from 'client/store/ui/actions'
import { setMode as setLoginModalMode } from 'client/store/login-modal/actions'
import CustomDashboardLimitErrorModal from 'client/components/modals/CustomDashboardLimitErrorModal'
import TrialOverModal from 'client/components/modals/TrialOverModal'

const ReferenceCheckModal = loadable(() => import('client/components/modals/ReferenceCheckModal'))
const LoginModal = loadable(() => import('client/components/modals/LoginModal'))
const ResetPasswordModal = loadable(() => import('client/components/modals/ResetPasswordModal'))
const UpgradeModal = loadable(() => import('client/components/modals/UpgradeModal'))

// This is to manage global app lifecycle stuff (eg analytics)
export class App extends React.Component {
  // Not sure why this is needed, React complains otherwise
  // Error state actually set in componentDidCatch
  static getDerivedStateFromError () {
    return { hasError: true }
  }

  handleKeydown = ({ key }) => {
    if (key === 'Enter') {
      const active = document.activeElement
      active && active.click()
    }
  }

  checkForExtension = () => {
    const { extensionLoaded } = this.props
    const marker = document.getElementById('scite-extension-marker')
    if (marker) {
      extensionLoaded()
    }
  }

  checkForSharedBy = () => {
    const { location } = this.props
    const query = qs.parse(location.search)
    const { localStorage } = window
    if (localStorage && query.shared_by) {
      localStorage.setItem('sharedBy', query.shared_by)
    }
  }

  componentDidMount () {
    const {
      loadUser,
      loadBrowser,
      toggleModal,
      handleReferral,
      setLoginModalMode,
      checkOrganizationLicense,
      loadAnonToken,
      history,
      user,
      hasUser,
      location: { search }
    } = this.props
    const query = qs.parse(search)

    if (query.signup) {
      setLoginModalMode('register')
      toggleModal('login', true)
    } else if (query.login) {
      setLoginModalMode('login')
      toggleModal('login', true)
    }

    analytics.setup()
    analytics.pageView()
    history.listen(() => analytics.pageView())
    history.listen(rollbar.handlePageChange)

    crisp.setup()
    rollbar.setup()

    if (user) {
      analytics.setUser(user)
    }

    if (hasUser) {
      loadUser({ query })
    }
    loadBrowser()
    checkOrganizationLicense()
    initializePostHog()

    if (query.authToken) {
      loadAnonToken(query.authToken)
    }

    //
    // Check first in case the event already fired
    // which I assume is possible.
    //
    this.checkForExtension()
    window.addEventListener('scite-extension/loaded', this.checkForExtension)

    handleReferral()

    window.addEventListener('keydown', this.handleKeydown)

    this.checkForSharedBy()
  }

  componentDidUpdate (prevProps) {
    const {
      user,
      organizationLicense,
      setBannerContent
    } = this.props

    if (organizationLicense?.hasLicense) {
      setBannerContent('organizationLicense')
    }

    if (user && user !== prevProps.user) {
      analytics.setUser(user)
    }
  }

  componentWillUnmount () {
    window.removeEventListener('keydown', this.handleKeydown)
    window.removeEventListener('scite-extension/loaded', this.checkForExtension)
  }

  componentDidCatch (appError, info) {
    const { setPageError } = this.props
    console.error(appError)
    Rollbar.error('React uncaught error', appError)
    setPageError({ status: 500 })
  }

  render = () => {
    const { shownModals } = this.props

    const modals = [
      ['login', LoginModal],
      ['upgrade', UpgradeModal],
      ['resetPassword', ResetPasswordModal],
      ['referenceCheck', ReferenceCheckModal]
    ]

    return (
      <>
        <Head />

        <Main />

        {
          modals
            .filter(([name, Modal]) => shownModals.includes(name))
            .map(([name, Modal]) => (
              <Modal key={name} />
            ))
        }
        <CustomDashboardLimitErrorModal />
        <TrialOverModal />
        <Toaster />
      </>
    )
  }
}

const mapStateToProps = ({ ui }) => ({
  user: ui.user,
  hasUser: ui.hasUser,
  fetchingUser: ui.fetchingUser,
  shownModals: ui.shownModals,
  organizationLicense: ui.organizationLicense
})
const actionCreators = {
  loadUser,
  loadBrowser,
  handleReferral,
  toggleModal,
  setLoginModalMode,
  setPageError,
  extensionLoaded,
  checkOrganizationLicense,
  setBannerContent,
  loadAnonToken
}
const WrappedApp = compose(
  connect(mapStateToProps, actionCreators),
  withRouter
)(App)

const AppWithStore = ({ store, ...rest }) => (
  <Provider store={store}>
    <WrappedApp {...rest} />
  </Provider>
)

export default AppWithStore
